// The in-page Copilot panel. Built from src/design tokens only, like the
// full Copilot page, so nothing here is a new colour.
import styled, { css, createGlobalStyle, keyframes } from "styled-components";
import {
  FONT, FIG_INK, FIG_MUTED, PANEL_BORDER, CARD_BORDER, HAIRLINE, PRIMARY, PRIMARY_SOFT, RADIUS_CTA,
} from "../../design";

export const PANEL_WIDTH = 400;
// Wide enough to dock beside the page without squeezing it (the study page
// header needs about 1000 px); below this the panel floats over the page.
export const DOCK_MIN_WIDTH = 1600;

export const Panel = styled.aside`
  font-family: ${FONT};
  color: ${FIG_INK};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${PANEL_WIDTH}px;
  max-width: 100vw;
  background: #fff;
  border-left: 1px solid ${PANEL_BORDER};
  display: flex;
  flex-direction: column;
  z-index: 1000;
  ${(p) => (p.$floating ? css`box-shadow: -12px 0 32px rgba(44, 41, 38, 0.12);` : "")}
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 10px 10px 16px;
  border-bottom: 1px solid ${HAIRLINE};
  min-height: 52px;
`;

export const HeadTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IconButton = styled.button`
  all: unset;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${FIG_MUTED};
  font-size: 15px;
  &:hover { background: ${PRIMARY_SOFT}; color: ${FIG_INK}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 1px; }
  &:disabled { opacity: 0.4; cursor: default; background: none; }
`;

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 6px;
`;

export const Empty = styled.div`
  padding: 28px 8px 8px;
  text-align: center;
  h3 { font-size: 15px; font-weight: 600; margin: 0 0 6px; }
  p { font-size: 13px; color: ${FIG_MUTED}; margin: 0 0 16px; line-height: 1.5; }
`;

export const Chips = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  button {
    all: unset;
    cursor: pointer;
    text-align: left;
    font-size: 13px;
    line-height: 1.4;
    padding: 9px 12px;
    border: 1px solid ${CARD_BORDER};
    border-radius: ${RADIUS_CTA}px;
    &:hover { border-color: ${PRIMARY}; color: ${PRIMARY}; }
    &:focus-visible { outline: 2px solid ${PRIMARY}; }
  }
`;

export const Where = styled.div`
  font-size: 11px;
  color: ${FIG_MUTED};
  margin-top: 4px;
  padding: 0 4px;
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

export const OpenButton = styled.button`
  all: unset;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: ${PRIMARY};
  border: 1px solid rgba(46, 69, 218, 0.35);
  border-radius: 999px;
  padding: 5px 12px;
  &:hover { background: ${PRIMARY_SOFT}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; }
`;

// A clarifying question's answers: the Open button's pill, picked or spent.
export const ChoiceButton = styled(OpenButton)`
  ${(p) => p.$picked && css`background: ${PRIMARY_SOFT};`}
  &:disabled { cursor: default; opacity: ${(p) => (p.$picked ? 1 : 0.5)}; }
  &:disabled:hover { background: ${(p) => (p.$picked ? PRIMARY_SOFT : "transparent")}; }
`;

export const ChoiceQuestion = styled.div`
  font-size: 12.5px;
  color: ${FIG_MUTED};
  margin-top: 10px;
`;

export const Composer = styled.div`
  border-top: 1px solid ${HAIRLINE};
  padding: 10px 12px 12px;
  position: relative;
`;

export const ContextRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const ContextChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  font-size: 11.5px;
  color: ${(p) => (p.$ref ? PRIMARY : FIG_MUTED)};
  background: ${(p) => (p.$ref ? PRIMARY_SOFT : "rgba(109, 106, 103, 0.08)")};
  border-radius: 999px;
  padding: 3px 4px 3px 10px;
  span.t { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 260px; }
  button {
    all: unset; cursor: pointer; width: 16px; height: 16px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center; font-size: 11px;
    &:hover { background: rgba(44, 41, 38, 0.1); }
    &:focus-visible { outline: 2px solid ${PRIMARY}; }
  }
`;

export const RefMenu = styled.ul`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% - 4px);
  max-height: 260px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid ${CARD_BORDER};
  border-radius: ${RADIUS_CTA}px;
  box-shadow: 0 8px 24px rgba(44, 41, 38, 0.12);
  z-index: 2;
  li {
    padding: 7px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    &[aria-selected="true"], &:hover { background: ${PRIMARY_SOFT}; }
    small { font-size: 11px; color: ${FIG_MUTED}; }
  }
  li.none { cursor: default; color: ${FIG_MUTED}; background: none; }
`;

export const Hint = styled.div`
  font-size: 11px;
  color: ${FIG_MUTED};
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
`;

// Its own surface: a dropdown with no background lets the chat show through.
export const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px;
  background: #fff;
  border: 1px solid ${CARD_BORDER};
  border-radius: ${RADIUS_CTA}px;
  box-shadow: 0 8px 24px rgba(44, 41, 38, 0.12);
  width: 300px;
  max-height: 360px;
  overflow-y: auto;
  li {
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    flex-direction: column;
    &:hover, &[aria-current="true"] { background: ${PRIMARY_SOFT}; }
    small { font-size: 11px; color: ${FIG_MUTED}; }
  }
`;

// A small round button, so it covers as little of the page as possible.
export const Launcher = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 999;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${PRIMARY};
  box-shadow: 0 6px 20px rgba(46, 69, 218, 0.32);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(46, 69, 218, 0.4); }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 3px; }
  img { width: 22px; height: 22px; }
  @media (prefers-reduced-motion: reduce) { transition: none; &:hover { transform: none; } }
`;

// The page's next-step chips, above the composer once a conversation runs.
// Next-step chips wrap onto a second line instead of running off the edge.
export const SuggestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-bottom: 8px;
  margin-bottom: 2px;
  button {
    all: unset;
    cursor: pointer;
    flex: none;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
    color: ${PRIMARY};
    border: 1px solid rgba(46, 69, 218, 0.3);
    border-radius: 999px;
    padding: 4px 10px;
    white-space: nowrap;
    &:hover { background: ${PRIMARY_SOFT}; }
    &:focus-visible { outline: 2px solid ${PRIMARY}; }
  }
`;

// "Show me where": the control Copilot points at pulses for a few seconds.
const ring = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(46, 69, 218, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(46, 69, 218, 0); }
  100% { box-shadow: 0 0 0 0 rgba(46, 69, 218, 0); }
`;
export const HighlightStyle = createGlobalStyle`
  .copilot-highlight {
    outline: 2px solid ${PRIMARY} !important;
    outline-offset: 3px;
    border-radius: 8px;
    animation: ${ring} 1.4s ease-out 3;
  }
  @media (prefers-reduced-motion: reduce) {
    .copilot-highlight { animation: none; }
  }
`;

// Dictation, beside the send button inside the composer.
export const MicButton = styled.button`
  all: unset;
  cursor: pointer;
  position: absolute;
  right: 52px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${(p) => (p.$on ? "#fff" : FIG_MUTED)};
  background: ${(p) => (p.$on ? PRIMARY : "transparent")};
  &:hover { background: ${(p) => (p.$on ? PRIMARY : PRIMARY_SOFT)}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; }
`;
